'use client'

import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useMutation, useQuery } from '@apollo/client'
import { SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { SheetScrollableContent } from '@/components/ui/sheet-scrollable-content'
import LanguageSwitcher from '@/components/shared/languageSelect'
import { useLanguage } from '@/context/LanguageContext'
import '@/components/styles/Form.css'
import InputFieldContainer from '@/components/shared/inputComponents/InputFieldContainer'
import { ImageUpload } from '@/components/shared/inputComponents/ImageUpload'
import {
  CREATE_CONTENT_BLOCK,
  GET_CONTENT_BLOCK,
  UPDATE_CONTENT_BLOCK,
} from '../contentBlockQueries'
import { ContentBlockFormValues, ContentBlockType, ContentBlockVideoType } from '../types'

const imageTypes: ContentBlockType[] = ['only_image', 'text_and_image']
const videoTypes: ContentBlockType[] = ['only_video', 'text_and_video']

const videoTypeOptions: { value: ContentBlockVideoType; label: string }[] = [
  { value: 'youtube', label: 'YouTube' },
  { value: 'vimeo', label: 'Vimeo' },
  { value: 'uploaded', label: 'Uploaded' },
]

const contentBlockTypes: { value: ContentBlockType; label: string }[] = [
  { value: 'only_text', label: 'Text Only' },
  { value: 'only_image', label: 'Image Only' },
  { value: 'text_and_image', label: 'Text & Image' },
  { value: 'only_video', label: 'Video Only' },
  { value: 'text_and_video', label: 'Text & Video' },
]

const formSchema = z.object({
  content_block_profiles: z
    .array(
      z.object({
        id: z.number().optional(),
        name: z.string().optional(),
        description: z.string().optional(),
        language: z.string(),
      })
    )
    .transform((items, ctx) => {
      const filteredItems = items.filter(item => item.name?.trim() !== '')
      if (filteredItems.length === 0) {
        items.forEach((item, index) => {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'At least one language profile needs to be filled',
            path: [`[${index}].name`],
          })
        })
      }
      return filteredItems
    }),
  content_block_type: z.enum([
    'only_text',
    'only_image',
    'text_and_image',
    'only_video',
    'text_and_video',
  ]),
  images: z.array(z.string()),
  selected_products: z.array(z.object({ id: z.number() })),
  video_link: z.string().optional(),
  video_type: z.enum(['youtube', 'vimeo', 'uploaded']).optional(),
  video_autoplay: z.boolean(),
  video_loop: z.boolean(),
  default_margins: z.boolean(),
  object_fit_cover: z.boolean(),
}) satisfies z.ZodType<ContentBlockFormValues>

interface ContentBlockFormProps {
  data: {
    id: number | null
  }
  onDirtyChange?: (dirty: boolean) => void
}

export default function ContentBlockForm({
  data,
  onDirtyChange,
}: ContentBlockFormProps) {
  const { allLanguages, languageId } = useLanguage()
  const [id, setId] = useState(data?.id)

  const {
    data: { findUniqueContentBlock: contentBlockData } = {},
    loading: loadingData,
  } = useQuery(GET_CONTENT_BLOCK, {
    variables: {
      id: id,
    },
    fetchPolicy: 'network-only',
    skip: !id,
  })

  const form = useForm<ContentBlockFormValues>({
    resolver: zodResolver(formSchema),
    mode: 'onSubmit',
    defaultValues: {
      content_block_profiles: [],
      content_block_type: 'only_text',
      images: [],
      selected_products: [],
      video_link: '',
      video_type: undefined,
      video_autoplay: false,
      video_loop: false,
      default_margins: true,
      object_fit_cover: false,
    },
  })

  const selectedType = form.watch('content_block_type')
  const showImageUpload = imageTypes.includes(selectedType)
  const showVideoInput = videoTypes.includes(selectedType)

  const [updateContentBlock] = useMutation(UPDATE_CONTENT_BLOCK, {
    onError: () => {
      toast.error('Failed to update content block')
    },
    onCompleted: () => {
      toast.success('Content block updated successfully')
    },
    refetchQueries: ['GET_CONTENT_BLOCKS', 'GetContentBlock'],
  })

  const [createContentBlock] = useMutation(CREATE_CONTENT_BLOCK, {
    onError: () => {
      toast.error('Failed to create content block')
    },
    onCompleted: response => {
      toast.success('Content block created successfully')
      const newId = response.createNewContentBlock.id
      setId(newId)
    },
    refetchQueries: ['GET_CONTENT_BLOCKS'],
  })

  function onSubmit(values: ContentBlockFormValues) {
    const variables = {
      content_block_profiles: values.content_block_profiles.map(p => ({
        id: p.id,
        name: p.name,
        description: p.description,
        language: p.language,
      })),
      content_block_type: values.content_block_type,
      images: values.images,
      selected_products: values.selected_products,
      video_link: values.video_link || null,
      video_type: values.video_type || null,
      video_autoplay: values.video_autoplay,
      video_loop: values.video_loop,
      default_margins: values.default_margins,
      object_fit_cover: values.object_fit_cover,
    }

    if (id) {
      updateContentBlock({
        variables: {
          where: { id },
          ...variables,
        },
      })
    } else {
      createContentBlock({
        variables,
      })
    }
  }

  useEffect(() => {
    if (!loadingData) {
      if (contentBlockData) {
        const existingProfiles =
          contentBlockData.content_block_profiles?.filter(
            (p: { language: string }) => p.language && allLanguages.includes(p.language)
          ) ?? []

        const allProfiles = allLanguages.map(lang => {
          const existingProfile = existingProfiles.find(
            (p: { language: string }) => p.language === lang
          )
          if (existingProfile) {
            return {
              id: existingProfile.id,
              name: existingProfile.name ?? '',
              description: existingProfile.description ?? '',
              language: existingProfile.language,
            }
          } else {
            return {
              name: '',
              description: '',
              language: lang,
            }
          }
        })

        form.reset({
          content_block_profiles: allProfiles,
          content_block_type: contentBlockData.type || 'only_text',
          images: contentBlockData.main_image
            ? [contentBlockData.main_image.src]
            : [],
          selected_products: contentBlockData.product_containers || [],
          video_link: contentBlockData.video_link || '',
          video_type: contentBlockData.video_type || undefined,
          video_autoplay: false,
          video_loop: false,
          default_margins: true,
          object_fit_cover: false,
        })
      } else {
        form.reset({
          content_block_profiles: allLanguages.map(lang => ({
            name: '',
            description: '',
            language: lang,
          })),
          content_block_type: 'only_text',
          images: [],
          selected_products: [],
          video_link: '',
          video_type: undefined,
          video_autoplay: false,
          video_loop: false,
          default_margins: true,
          object_fit_cover: false,
        })
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentBlockData, loadingData, allLanguages])

  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(form.formState.isDirty)
    }
  }, [form.formState.isDirty, onDirtyChange])

  return (
    <SheetContent className="sheet-content flex flex-col">
      <SheetHeader className="flex-shrink-0">
        <div className="flex justify-between items-center">
          <SheetTitle>Content Block</SheetTitle>
          <LanguageSwitcher />
        </div>
      </SheetHeader>
      <SheetScrollableContent>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4 max-w-3xl mx-auto py-5"
          >
            <div className="grid gap-4 py-4">
              <InputFieldContainer
                label="Type"
                description="Select the content block type"
                control={form.control}
                name="content_block_type"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {contentBlockTypes.map(type => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Name"
                description="Display name for this content block"
                control={form.control}
                name={`content_block_profiles.${languageId}.name`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input placeholder="" type="text" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Description"
                control={form.control}
                name={`content_block_profiles.${languageId}.description`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Textarea
                        placeholder="Description"
                        className="resize-none"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {showImageUpload && (
                <InputFieldContainer
                  label="Image"
                  description="Upload content block image"
                  control={form.control}
                  name="images"
                  render={field => (
                    <FormItem className="col-span-3">
                      <FormControl>
                        <ImageUpload maxImages={1} variant="large" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {showVideoInput && (
                <>
                  <InputFieldContainer
                    label="Video Type"
                    description="Select the video source type"
                    control={form.control}
                    name="video_type"
                    render={({ field }) => (
                      <FormItem className="col-span-3">
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select video type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {videoTypeOptions.map(type => (
                              <SelectItem key={type.value} value={type.value}>
                                {type.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <InputFieldContainer
                    label="Video Link"
                    description="Enter the video URL (YouTube or Vimeo link)"
                    control={form.control}
                    name="video_link"
                    render={({ field }) => (
                      <FormItem className="col-span-3">
                        <FormControl>
                          <Input
                            placeholder="https://www.youtube.com/watch?v=..."
                            type="text"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </>
              )}

              <Button type="submit" disabled={loadingData}>
                {id ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </Form>
      </SheetScrollableContent>
    </SheetContent>
  )
}
